import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ } from '@utils/dateUtils';
import { rollingMonthsAgo, todayCap } from '../../utils/dateRules';
import { useAbsentDates } from '../../hooks/useAbsentDates';
import type { IEntityKind } from '../IncidentReportCard';

interface Params {
  siteId: string;
  entityKind: IEntityKind;
  entityId: string | null;
}

interface Result {
  minDate: Date;
  maxDate: Date;
  disabledDates: Date[] | undefined;
  disabled: boolean;
}

/**
 * Bundles all the date-input constraints for the Daily Route card so the
 * card body stays declarative. Composes:
 *   - rolling 4-month minimum (matches Trusstor's investigation window)
 *   - today as the upper bound
 *   - per-tag absent dates (only when the user is filtering by tag)
 *   - the picker stays disabled until an entity is chosen, mirroring the
 *     Trusstor pattern where there's nothing meaningful to pick a date
 *     against without a subject.
 *
 * The working-site tz is read here once and threaded into every primitive
 * that needs it. The fallback to `APP_DEFAULT_TZ` is only hit during the
 * bootstrap window before the site selector has hydrated.
 */
export function useDailyRouteDateRules({ siteId, entityKind, entityId }: Params): Result {
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;
  const minDate = useMemo(() => rollingMonthsAgo(4, tz), [tz]);
  const maxDate = useMemo(() => todayCap(tz), [tz]);

  const absent = useAbsentDates({
    siteId,
    tagId: entityKind === 'tag' ? entityId : null,
    // Both bounds are tz-naive calendar days (`tzCalendarDayAsLocal`); the
    // hook reads their local fields directly, so no tz is threaded in.
    fromDate: minDate,
    toDate: maxDate,
  });

  return {
    minDate,
    maxDate,
    disabledDates: entityKind === 'tag' ? absent.data : undefined,
    disabled: !entityId,
  };
}
